"use client";

import { motion } from "motion/react";
import { ReactNode } from "react";

interface AdminContextBannerProps {
  /** Icon rendered in the badge, e.g. `<ShieldCheck className="h-4 w-4" strokeWidth={2} aria-hidden="true" />`. */
  icon: ReactNode;
  /** Full banner text, e.g. `Admin: launched for <span className="font-semibold">{orgName}</span>`. */
  children: ReactNode;
  /** `"center"` (default) for a single-line message; `"start"` top-aligns the icon badge with text
   *  that can wrap to 2+ lines, e.g. `AdminEntryGate`'s stale-org notice. */
  align?: "center" | "start";
  /** Optional id, e.g. so a heading elsewhere on the page can `aria-describedby` this banner. */
  id?: string;
}

/**
 * Shared shell for the persistent "Admin: ..." notices shown across every step of the wizard when a
 * Nowadays admin launches `/create` scoped to an org and/or owner (ENG-918) — {@link AdminOrgBanner}
 * (org-only) is a thin wrapper over this; `CreateEventWizard` renders this directly for the combined
 * org+owner case. Deliberately doesn't auto-dismiss or move, unlike `ResumeDraftBanner`, since the
 * "who is this for" context stays relevant for the whole fill, not just the first render.
 *
 * @param props.icon - Icon rendered in the badge.
 * @param props.children - The banner's text content.
 * @param props.align - Icon/text alignment; `"start"` for wrappable text.
 * @param props.id - Optional id for external `aria-describedby` references.
 * @returns The banner element.
 */
export const AdminContextBanner = ({ icon, children, align = "center", id }: AdminContextBannerProps) => {
  return (
    <motion.div
      id={id}
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className={`flex gap-3 rounded-2xl bg-amber-50 px-4 py-3 text-left ring-1 ring-inset ring-amber-200 ${
        align === "start" ? "items-start" : "items-center"
      }`}
      role="status"
    >
      <span
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white text-amber-600 ${
          align === "start" ? "mt-0.5" : ""
        }`}
      >
        {icon}
      </span>
      <p className="text-sm font-semibold text-gray-900 tracking-tight">{children}</p>
    </motion.div>
  );
};
